import { useRef } from 'react';
import { ChatInput } from './chat/ChatInput';
import { ChatControls } from './chat/ChatControls';
import FilePreview from './chat/FilePreview';
import { ChatHistory } from './chat/ChatHistory';
import { useChatState } from '@/hooks/useChatState';
import { useMessageHandling } from '@/hooks/useMessageHandling';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';

const TEXTAREA_MIN_HEIGHT = 44;
const TEXTAREA_MAX_HEIGHT = 200;

export const ChatPanel = () => {
  const {
    message,
    setMessage,
    uploadedFiles,
    imageDataList,
    isLoading,
    setIsLoading,
    messages,
    setMessages,
    clearState,
    addFile,
    removeFile
  } = useChatState();

  const { handleSendMessage } = useMessageHandling(
    messages,
    setMessages,
    setIsLoading,
    clearState
  );

  const {
    isListening,
    startListening,
    stopListening,
    recognition
  } = useSpeechRecognition(setMessage);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Image = e.target?.result as string;
          addFile(file, base64Image);
        };
        reader.readAsDataURL(file);
      }
    };

    input.click();
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64Image = e.target?.result as string;
            addFile(file, base64Image);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.currentTarget.style.border = '1px solid hsl(var(--border))';

    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Image = e.target?.result as string;
          addFile(file, base64Image);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const onSendMessage = () => {
    if (message.trim() || uploadedFiles.length > 0) {
      handleSendMessage(message);
      if (recognition) {
        recognition.abort();
        stopListening();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <ChatHistory messages={messages} />
        <FilePreview
          files={uploadedFiles}
          imageDataList={imageDataList}
          onRemove={removeFile}
        />
      </div>
      <div className="p-4 border-t border-border">
        <div className="relative">
          <ChatInput
            textareaRef={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onDrop={handleDrop}
            minHeight={TEXTAREA_MIN_HEIGHT}
            maxHeight={TEXTAREA_MAX_HEIGHT}
          />
          <ChatControls
            onFileUpload={handleFileUpload}
            onSendMessage={onSendMessage}
            isListening={isListening}
            onStartListening={startListening}
            onStopListening={stopListening}
            isSendDisabled={!message.trim() && uploadedFiles.length === 0 || isLoading}
            messageLength={message.length}
          />
        </div>
        {message.length > 3 && (
          <div className="mt-2 text-xs text-muted-foreground">
            Нажмите <kbd className="px-1.5 py-0.5 rounded bg-muted">Shift</kbd> + <kbd className="px-1.5 py-0.5 rounded bg-muted">Enter</kbd> для новой строки
          </div>
        )}
      </div>
    </div>
  );
};